import React, { useState, useEffect, useRef } from 'react'
import { cn } from '@duck/ui'

interface GradeBadgeProps {
  grade: number
  small?: boolean
  className?: string
}

const useCountUp = (targetValue: number, duration = 1000): number => {
  const [displayValue, setDisplayValue] = useState(0)
  const startTimeRef = useRef<number | null>(null)
  const hasAnimatedRef = useRef(false)

  useEffect(() => {
    if (hasAnimatedRef.current) return
    hasAnimatedRef.current = true

    const animate = (currentTime: number) => {
      if (startTimeRef.current === null) {
        startTimeRef.current = currentTime
      }

      const elapsed = currentTime - startTimeRef.current
      const progress = Math.min(elapsed / duration, 1)

      // Ease-out cubic for smooth deceleration
      const easeOutProgress = 1 - Math.pow(1 - progress, 3)
      const currentValue = easeOutProgress * targetValue

      // Preserve a single decimal if the target has one, otherwise stay integral
      const hasDecimal = !Number.isInteger(targetValue)
      setDisplayValue(
        progress < 1
          ? hasDecimal
            ? Math.round(currentValue * 10) / 10
            : Math.round(currentValue)
          : targetValue
      )

      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }

    requestAnimationFrame(animate)
  }, [targetValue, duration])

  return displayValue
}

type Tier = 'excellent' | 'good' | 'fair' | 'low' | 'critical'

const getTier = (grade: number): Tier => {
  // Grade is on a 0-10 scale
  if (grade >= 8.5) return 'excellent'
  if (grade >= 7) return 'good'
  if (grade >= 5) return 'fair'
  if (grade >= 2) return 'low'
  return 'critical'
}

interface TierStyle {
  text: string
  ring: string
  surface: string
  fill: string
  track: string
  /** Outer glow — only applied on the full-size variant so dense tables stay calm. */
  glow: string
}

// Literal class strings per tier so Tailwind's JIT can see every utility.
const TIER_STYLES: Record<Tier, TierStyle> = {
  excellent: {
    text: 'text-success',
    ring: 'ring-success/35',
    surface: 'bg-success/10',
    fill: 'bg-success',
    track: 'bg-success/15',
    glow: 'shadow-[0_6px_28px_-6px_rgb(34_197_94/0.55)]',
  },
  good: {
    text: 'text-blue-500 dark:text-blue-400',
    ring: 'ring-blue-500/35',
    surface: 'bg-blue-500/10',
    fill: 'bg-blue-500',
    track: 'bg-blue-500/15',
    glow: 'shadow-[0_6px_28px_-6px_rgb(59_130_246/0.5)]',
  },
  fair: {
    text: 'text-amber-500',
    ring: 'ring-amber-500/35',
    surface: 'bg-amber-500/10',
    fill: 'bg-amber-500',
    track: 'bg-amber-500/15',
    glow: 'shadow-[0_6px_28px_-6px_rgb(245_158_11/0.45)]',
  },
  low: {
    text: 'text-foreground/80',
    ring: 'ring-border',
    surface: 'bg-muted-foreground/10',
    fill: 'bg-muted-foreground/55',
    track: 'bg-muted-foreground/15',
    glow: 'shadow-md shadow-black/20',
  },
  critical: {
    text: 'text-destructive',
    ring: 'ring-destructive/40',
    surface: 'bg-destructive/10',
    fill: 'bg-destructive',
    track: 'bg-destructive/15',
    glow: 'shadow-[0_6px_28px_-6px_rgb(239_68_68/0.45)]',
  },
}

export const GradeBadge: React.FC<GradeBadgeProps> = ({
  grade,
  small = false,
  className,
}) => {
  const displayGrade = useCountUp(grade, 1100)

  // Drive the progress bar from 0 → grade once on mount via a CSS width transition.
  const [filled, setFilled] = useState(false)
  useEffect(() => {
    const raf = requestAnimationFrame(() => setFilled(true))
    return () => cancelAnimationFrame(raf)
  }, [])

  const tier = getTier(grade)
  const styles = TIER_STYLES[tier]
  const fillPct = Math.max(0, Math.min(grade / 10, 1)) * 100
  // Render a clean integer when whole, otherwise one decimal (e.g. 7.5).
  const shown = Number.isInteger(displayGrade)
    ? displayGrade.toString()
    : displayGrade.toFixed(1)

  const numeral = (
    <div className="relative flex items-baseline gap-0.5">
      <span
        className={cn(
          'font-jet-mono font-bold leading-none tabular-nums tracking-tight',
          styles.text,
          small ? 'text-base' : 'text-[2rem]'
        )}
      >
        {shown}
      </span>
      <span
        className={cn(
          'font-jet-mono font-medium leading-none tabular-nums opacity-45',
          styles.text,
          small ? 'text-[10px]' : 'text-xs'
        )}
      >
        /10
      </span>
    </div>
  )

  // Progress track — reads the grade as a fraction of 10.
  const track = (
    <div
      className={cn(
        'relative overflow-hidden rounded-full',
        styles.track,
        // Hide the bar on mobile to keep the chip tight in cramped table rows
        small ? 'hidden h-1 w-7 shrink-0 sm:block' : 'h-1 w-full'
      )}
    >
      <div
        className={cn(
          'h-full rounded-full transition-[width] duration-1000 ease-out',
          styles.fill
        )}
        style={{ width: filled ? `${fillPct}%` : '0%' }}
      />
    </div>
  )

  return (
    <div
      // Grade is data — keep numeral + bar in fixed LTR order regardless of locale direction
      dir="ltr"
      className={cn('inline-flex', className)}
      role="img"
      aria-label={`${grade} out of 10`}
    >
      <div
        className={cn(
          'group relative overflow-hidden ring-1 ring-inset backdrop-blur-sm',
          'transition-all duration-300 ease-out hover:-translate-y-0.5',
          styles.surface,
          styles.ring,
          small
            ? 'inline-flex items-center gap-2 rounded-lg px-2.5 py-1'
            : cn(
                'inline-flex flex-col items-center justify-center gap-1.5 rounded-2xl px-5 pb-3 pt-3.5',
                styles.glow
              )
        )}
      >
        {/* Lit-from-above inner highlight for a premium, dimensional surface */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/10 to-transparent"
        />

        {numeral}
        {track}
      </div>
    </div>
  )
}
